import { useParams, useNavigate, Link } from "react-router-dom";
import { Card, CardBody, CardHeader, Button, Skeleton } from "@heroui/react";
import { LuArrowLeft, LuPencil, LuPrinter } from "react-icons/lu";
import { useFetchData } from "@/hooks";
import {
    PageHeader,
    ThemedText,
    ErrorState,
    EmptyState,
} from "@/components/ui";
import { formatCurrency, formatDate } from "@/utils";
import { useUser } from "@/context/UserContext";

const SalesPaymentDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = useUser();

    const { data, isLoading, error, refetch } = useFetchData(
        `sales-payment/${id}/show`,
    );

    if (error) {
        const is404 = error?.response?.status === 404;
        return (
            <div className="p-6">
                {is404 ? (
                    <EmptyState
                        title="Data Tidak Ditemukan"
                        description="Data pelunasan yang Anda cari tidak ditemukan atau sudah dihapus."
                        actionLabel="Kembali ke Daftar"
                        onAction={() => navigate("/sales-payment")}
                    />
                ) : (
                    <ErrorState
                        title="Gagal Memuat Detail Pembayaran"
                        message={error?.message}
                        onRetry={refetch}
                    />
                )}
            </div>
        );
    }

    const payment = data;
    const items = payment?.items || [];
    const totalDiscount = items.reduce(
        (sum, item) => sum + (item.discount || 0),
        0,
    );

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Detail Pembayaran Penjualan"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Penjualan", href: "/sales-order" },
                        {
                            label: "Pembayaran Penjualan",
                            href: "/sales-payment",
                        },
                        isLoading
                            ? { label: "..." }
                            : { label: payment?.payment_number },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <Button
                        variant="flat"
                        size="sm"
                        startContent={<LuArrowLeft />}
                        onPress={() => navigate("/sales-payment")}
                    >
                        Kembali
                    </Button>
                    <Button
                        variant="flat"
                        size="sm"
                        startContent={<LuPrinter />}
                        onPress={() => navigate(`/sales-payment/${id}/print`)}
                    >
                        Print
                    </Button>
                    {hasPermission("sales-payment.edit") && (
                        <Button
                            as={Link}
                            to={`/sales-payment/${id}/edit`}
                            color="primary"
                            size="sm"
                            startContent={<LuPencil />}
                        >
                            Edit
                        </Button>
                    )}
                </div>
            </div>

            {/* Info Pembayaran */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="px-4 pt-4 pb-0">
                    <ThemedText size="md" className="font-semibold">
                        Informasi Pembayaran
                    </ThemedText>
                </CardHeader>
                <CardBody className="p-4">
                    {isLoading ? (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="flex flex-col gap-1">
                                    <Skeleton className="h-4 w-24 rounded-lg" />
                                    <Skeleton className="h-5 w-40 rounded-lg" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <InfoItem
                                label="No. Pembayaran"
                                value={payment?.payment_number}
                            />
                            <InfoItem
                                label="Tanggal"
                                value={formatDate(
                                    payment?.payment_date,
                                    "dd MMM yyyy",
                                )}
                            />
                            <InfoItem
                                label="Customer"
                                value={payment?.customer?.name || "-"}
                            />
                            <InfoItem
                                label="Kas / Bank"
                                value={payment?.master_cash?.name || "-"}
                            />
                            <InfoItem
                                label="Total Pembayaran"
                                value={formatCurrency(payment?.total_amount)}
                                bold
                            />
                            {payment?.description && (
                                <InfoItem
                                    label="Keterangan"
                                    value={payment.description}
                                />
                            )}
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Daftar Invoice */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="flex flex-col items-start gap-0.5 px-4 pt-4">
                    <ThemedText size="md" className="font-semibold">
                        Daftar Invoice
                    </ThemedText>
                    {!isLoading && (
                        <ThemedText size="xs" variant="caption">
                            {items.length} invoice
                        </ThemedText>
                    )}
                </CardHeader>
                <CardBody className="overflow-x-auto p-4 pt-2">
                    {isLoading ? (
                        <div className="flex flex-col gap-3">
                            {[...Array(3)].map((_, i) => (
                                <div
                                    key={i}
                                    className="flex w-full items-center gap-4"
                                >
                                    <Skeleton className="h-5 w-8 shrink-0 rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                    <Skeleton className="h-5 flex-[1.5] rounded-lg" />
                                    <Skeleton className="h-5 flex-[1.5] rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                </div>
                            ))}
                        </div>
                    ) : items.length === 0 ? (
                        <ThemedText
                            size="sm"
                            variant="secondary"
                            className="py-8 text-center"
                        >
                            Tidak ada invoice.
                        </ThemedText>
                    ) : (
                        <table className="themed-table">
                            <thead>
                                <tr>
                                    <th>No.</th>
                                    <th>No. Invoice</th>
                                    <th>Tanggal</th>
                                    <th>Status Bayar</th>
                                    <th>Keterangan</th>
                                    <th className="text-right">
                                        Total Invoice
                                    </th>
                                    <th className="text-right">Diskon</th>
                                    <th className="text-right">Jumlah Bayar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, index) => (
                                    <tr key={item.id}>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {index + 1}.
                                            </ThemedText>
                                        </td>
                                        <td>
                                            <ThemedText
                                                size="xs"
                                                className="font-medium"
                                            >
                                                {item.order_number || "-"}
                                            </ThemedText>
                                        </td>
                                        <td>
                                            <ThemedText size="xs">
                                                {item.order_date
                                                    ? formatDate(
                                                          item.order_date,
                                                          "dd MMM yyyy",
                                                      )
                                                    : "-"}
                                            </ThemedText>
                                        </td>
                                        <td>
                                            <ThemedText size="xs">
                                                {item.payment_status || "-"}
                                            </ThemedText>
                                        </td>
                                        <td>
                                            <ThemedText
                                                size="xs"
                                                variant="secondary"
                                            >
                                                {item.description || "-"}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText size="xs">
                                                {formatCurrency(
                                                    item.so_total_amount,
                                                )}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText
                                                size="xs"
                                                className={
                                                    item.discount > 0
                                                        ? "text-danger"
                                                        : ""
                                                }
                                            >
                                                {item.discount > 0
                                                    ? formatCurrency(
                                                          item.discount,
                                                      )
                                                    : "-"}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText
                                                size="xs"
                                                className="font-medium"
                                            >
                                                {formatCurrency(item.amount)}
                                            </ThemedText>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={6} className="text-right">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            TOTAL
                                        </ThemedText>
                                    </td>
                                    <td className="text-right">
                                        <ThemedText
                                            size="xs"
                                            className="text-danger font-bold"
                                        >
                                            {totalDiscount > 0
                                                ? formatCurrency(totalDiscount)
                                                : "-"}
                                        </ThemedText>
                                    </td>
                                    <td className="text-right">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatCurrency(
                                                payment?.total_amount,
                                            )}
                                        </ThemedText>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </CardBody>
            </Card>

            {/* Catatan */}
            {!isLoading && payment && (
                <div className="space-y-1 px-1 leading-relaxed">
                    <ThemedText
                        size="xs"
                        variant="secondary"
                        className="font-medium"
                    >
                        Catatan:
                    </ThemedText>
                    <ol className="text-default-400 marker:text-default-400 list-disc space-y-0.5 pl-4">
                        <li>
                            <ThemedText size="xs" variant="secondary">
                                Dokumen ini merupakan bukti pelunasan yang sah
                                atas invoice yang tercantum di atas.
                            </ThemedText>
                        </li>
                        <li>
                            <ThemedText size="xs" variant="secondary">
                                Pembayaran yang telah dicatat tidak dapat
                                dibatalkan tanpa persetujuan pihak yang
                                berwenang.
                            </ThemedText>
                        </li>
                    </ol>
                </div>
            )}

            {/* Footer metadata */}
            {!isLoading && payment && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1">
                    <ThemedText size="xs" variant="secondary">
                        Dibuat:{" "}
                        {formatDate(payment.created_at, "dd MMM yyyy HH:mm")}
                    </ThemedText>
                    <ThemedText size="xs" variant="secondary">
                        ·
                    </ThemedText>
                    <ThemedText size="xs" variant="secondary">
                        Terakhir diubah:{" "}
                        {formatDate(payment.updated_at, "dd MMM yyyy HH:mm")}
                    </ThemedText>
                </div>
            )}
        </div>
    );
};

const InfoItem = ({ label, value, bold = false }) => (
    <div className="flex flex-col gap-1">
        <ThemedText size="xs" variant="secondary" className="font-medium">
            {label}
        </ThemedText>
        <ThemedText size="sm" className={bold ? "font-semibold" : ""}>
            {value}
        </ThemedText>
    </div>
);

export default SalesPaymentDetail;
